import { useState } from 'react';
import { Button } from '../common/Button';
import { DateField } from '../ui/DateField';
import { formatMonthYear } from '../../utils/dateFormat';

interface ReportFiltersProps {
  onApply: (filters: { from: string; to: string }) => void;
  isLoading?: boolean;
  initialFrom?: string;
  initialTo?: string;
  children?: React.ReactNode;
}

function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDate(dateStr: string): Date {
  const parts = dateStr.split('-');
  const year = parseInt(parts[0] ?? '0', 10);
  const month = parseInt(parts[1] ?? '1', 10) - 1;
  const day = parseInt(parts[2] ?? '1', 10);
  return new Date(year, month, day);
}

function getDefaultDates() {
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from: formatDate(firstOfMonth),
    to: formatDate(lastOfMonth),
  };
}

function getPreviousMonthDates(currentFrom: string) {
  const current = parseDate(currentFrom);
  const firstOfPrevMonth = new Date(current.getFullYear(), current.getMonth() - 1, 1);
  const lastOfPrevMonth = new Date(current.getFullYear(), current.getMonth(), 0);
  return {
    from: formatDate(firstOfPrevMonth),
    to: formatDate(lastOfPrevMonth),
  };
}

function getNextMonthDates(currentFrom: string) {
  const current = parseDate(currentFrom);
  const firstOfNextMonth = new Date(current.getFullYear(), current.getMonth() + 1, 1);
  const lastOfNextMonth = new Date(current.getFullYear(), current.getMonth() + 2, 0);
  return {
    from: formatDate(firstOfNextMonth),
    to: formatDate(lastOfNextMonth),
  };
}

function getCurrentQuarterDates() {
  const now = new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3);
  const firstOfQuarter = new Date(now.getFullYear(), currentQuarter * 3, 1);
  const lastOfQuarter = new Date(now.getFullYear(), currentQuarter * 3 + 3, 0);
  return {
    from: formatDate(firstOfQuarter),
    to: formatDate(lastOfQuarter),
  };
}

function getPreviousQuarterDates() {
  const now = new Date();
  const currentQuarter = Math.floor(now.getMonth() / 3);
  let year = now.getFullYear();
  let prevQuarter = currentQuarter - 1;

  if (prevQuarter < 0) {
    prevQuarter = 3;
    year -= 1;
  }

  const firstOfQuarter = new Date(year, prevQuarter * 3, 1);
  const lastOfQuarter = new Date(year, prevQuarter * 3 + 3, 0);
  return {
    from: formatDate(firstOfQuarter),
    to: formatDate(lastOfQuarter),
  };
}

export default function ReportFilters({ onApply, isLoading, initialFrom, initialTo, children }: ReportFiltersProps) {
  const defaults = getDefaultDates();
  const [from, setFrom] = useState(initialFrom || defaults.from);
  const [to, setTo] = useState(initialTo || defaults.to);

  const handleApply = () => {
    if (from && to) {
      onApply({ from, to });
    }
  };

  const setDateRangeAndApply = (dates: { from: string; to: string }) => {
    setFrom(dates.from);
    setTo(dates.to);
    onApply(dates);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
          <select
            value=""
            onChange={(e) => {
              if (!e.target.value) return;
              const [y, m] = e.target.value.split('-').map(Number);
              const first = formatDate(new Date(y!, m! - 1, 1));
              const last = formatDate(new Date(y!, m!, 0));
              setFrom(first);
              setTo(last);
            }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select month...</option>
            {(() => {
              const options = [];
              const now = new Date();
              for (let i = 0; i < 12; i++) {
                const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
                const value = `${d.getFullYear()}-${d.getMonth() + 1}`;
                const label = formatMonthYear(d); // MKS-351 — "October 2026"
                options.push(<option key={value} value={value}>{label}</option>);
              }
              return options;
            })()}
          </select>
        </div>
        <div>
          <label htmlFor="report-filter-from" className="block text-sm font-medium text-gray-700 mb-1">From</label>
          <DateField
            id="report-filter-from"
            value={from}
            onChange={setFrom}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="report-filter-to" className="block text-sm font-medium text-gray-700 mb-1">To</label>
          <DateField
            id="report-filter-to"
            value={to}
            onChange={setTo}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setDateRangeAndApply(getPreviousMonthDates(from))}
            className="px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            &larr; Prev Month
          </button>
          <button
            type="button"
            onClick={() => setDateRangeAndApply(getNextMonthDates(from))}
            className="px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Next Month &rarr;
          </button>
          <button
            type="button"
            onClick={() => setDateRangeAndApply(getCurrentQuarterDates())}
            className="px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Current Quarter
          </button>
          <button
            type="button"
            onClick={() => setDateRangeAndApply(getPreviousQuarterDates())}
            className="px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Previous Quarter
          </button>
        </div>
        {children}
        <div className="ml-auto">
          <Button onClick={handleApply} isLoading={isLoading} disabled={!from || !to}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
}
